import { blogPosts } from '../mocks/blog';

/** Convert a display category name to a URL-safe slug */
export function categoryToSlug(category: string): string {
  return category
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // strip accents
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-');
}

/** Find the display name that matches a given slug */
export function slugToCategory(slug: string): string | undefined {
  const allCategories = [...new Set(blogPosts.map(p => p.category))];
  return allCategories.find(c => categoryToSlug(c) === slug);
}

export interface CategoryMeta {
  name: string;
  slug: string;
  count: number;
}

/** Return all unique categories sorted by post count (desc) */
export function getAllCategories(): CategoryMeta[] {
  const map = new Map<string, number>();
  blogPosts.forEach(p => {
    map.set(p.category, (map.get(p.category) ?? 0) + 1);
  });
  return Array.from(map.entries())
    .map(([name, count]) => ({ name, slug: categoryToSlug(name), count }))
    .sort((a, b) => b.count - a.count);
}

/** Return posts belonging to a specific category slug */
export function getPostsByCategory(categorySlug: string) {
  return blogPosts.filter(p => categoryToSlug(p.category) === categorySlug);
}

/** Simulated read counts — editorial relevance / traffic estimates */
export const POST_READ_COUNTS: Record<number, number> = {
  5: 18400,
  4: 14700,
  9: 11200,
  3: 9800,
  10: 8300,
  11: 7100,
  6: 5900,
  8: 4600,
  7: 3800,
  1: 2900,
  2: 2100,
};

export function formatReadCount(n: number): string {
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return String(Math.round(n));
}

/** Return the top N most-read posts, optionally excluding one by id */
export function getTopReadPosts(n = 3, excludeId?: number) {
  return [...blogPosts]
    .filter(p => excludeId === undefined || p.id !== excludeId)
    .sort((a, b) => (POST_READ_COUNTS[b.id] ?? 0) - (POST_READ_COUNTS[a.id] ?? 0))
    .slice(0, n);
}

/** Portuguese month name → 0-based month index */
const PT_MONTHS: Record<string, number> = {
  janeiro: 0, fevereiro: 1, março: 2, abril: 3,
  maio: 4, junho: 5, julho: 6, agosto: 7,
  setembro: 8, outubro: 9, novembro: 10, dezembro: 11,
};

/** Parse a display date like "15 Março 2026" into a JS Date */
export function parsePostDate(dateStr: string): Date | null {
  const parts = dateStr.trim().split(' ');
  if (parts.length !== 3) return null;
  const day   = parseInt(parts[0], 10);
  const month = PT_MONTHS[parts[1].toLowerCase()];
  const year  = parseInt(parts[2], 10);
  if (isNaN(day) || month === undefined || isNaN(year)) return null;
  return new Date(year, month, day);
}

/**
 * Returns true when the post was published within the last `daysThreshold` days.
 * Default threshold: 30 days.
 */
export function isNewPost(dateStr: string, daysThreshold = 30): boolean {
  const postDate = parsePostDate(dateStr);
  if (!postDate) return false;
  const diffDays = (Date.now() - postDate.getTime()) / 86_400_000;
  return diffDays >= 0 && diffDays <= daysThreshold;
}
